import { createTheme } from '@mui/material/styles'
import React from 'react'

import { Icon } from '@components/ui'

import Colors from './colors'

function avenirVariation(fontSize, lineHeight, fontWeight) {
  return {
    fontFamily: 'Avenir, sans-serif',
    fontSize,
    fontWeight,
    lineHeight,
  }
}

const typography = {
  avenir10: avenirVariation('10px', '16px', 400),
  avenir10Bold: avenirVariation('10px', '16px', 600),
  avenir11: avenirVariation('11px', '19px', 400),
  avenir11Bold: avenirVariation('11px', '19px', 600),
  avenir12: avenirVariation('12px', '18px', 400),
  avenir12Bold: avenirVariation('12px', '18px', 600),
  avenir13: avenirVariation('13px', '18px', 400),
  avenir13Bold: avenirVariation('13px', '18px', 600),
  avenir14: avenirVariation('14px', '22px', 400),
  avenir14Bold: avenirVariation('14px', '22px', 600),
  avenir16: avenirVariation('16px', '26px', 400),
  avenir16Bold: avenirVariation('16px', '26px', 600),
  avenir20: avenirVariation('20px', '28px', 400),
  avenir20Bold: avenirVariation('20px', '28px', 600),
  avenir24: avenirVariation('24px', '36px', 400),
  avenir24Bold: avenirVariation('24px', '36px', 600),
  avenir32: avenirVariation('32px', '44px', 200),
  avenir32Bold: avenirVariation('32px', '44px', 600),
}

const components = {
  MuiAlert: {
    defaultProps: {
      iconMapping: {
        error: React.createElement(Icon, { name: 'exclamation' }),
        info: React.createElement(Icon, { name: 'info' }),
        success: React.createElement(Icon, { name: 'check-circle' }),
        warning: React.createElement(Icon, { name: 'exclamation' }),
      },
    },
    styleOverrides: {
      root: ({ ownerState }) => ({
        ...(ownerState.variant === 'standard' && {
          ...typography.avenir14,
          backgroundColor: Colors[ownerState.severity]?.light,
          borderColor: Colors[ownerState.severity]?.dark,
          borderRadius: 6,
          borderStyle: 'solid',
          borderWidth: 1,

          '.MuiAlert-icon': {
            color: Colors[ownerState.severity]?.main,
          },

          '.MuiAlert-message': {
            color: Colors.common.gray3,
          },
        }),
      }),
    },
  },
  MuiAlertTitle: {
    styleOverrides: {
      root: {
        ...typography.avenir14Bold,
      },
    },
  },
  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        color: Colors.common.slate5,
        minWidth: '45px',

        '&.focused > .MuiSvgIcon-root': {
          boxShadow: `0 0 0 2px ${Colors.primary.main}`,
          borderRadius: '5px',
        },
      },
    },
  },
  MuiButton: {
    defaultProps: {
      disableRipple: true,
      variant: 'contained',
    },
    styleOverrides: {
      root: ({ ownerState }) => ({
        ...typography.avenir16Bold,
        borderRadius: 6,
        padding: '8px 16px',
        textTransform: 'none',
        transition: 'none',
        ...(ownerState.variant === 'contained' && {
          backgroundColor: Colors.brand.color,
          boxShadow: `0px 2px 0px ${Colors.brand.darkest}`,
          color: 'white',
          '&:hover': {
            backgroundColor: Colors.brand.light,
            boxShadow: `0px 2px 0px ${Colors.brand.darkest}`,
          },
        }),
        ...(ownerState.variant === 'outlined' && {
          backgroundColor: 'white',
          borderColor: Colors.common.slate4,
        }),
        ...(ownerState.size === 'small' && {
          ...typography.avenir13,
          padding: '3px 15px',
        }),
      }),
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        '.MuiFormHelperText-root': {
          marginLeft: 0,
          marginRight: 0,
        },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        ...typography.avenir13Bold,
        color: Colors.common.gray3,
        transform: 'none',
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'standard',
      fullWidth: true,
      InputProps: {
        disableUnderline: true,
      },
      InputLabelProps: {
        shrink: true,
      },
    },
    styleOverrides: {
      root: ({ ownerState }) => ({
        label: {
          ...typography.avenir13Bold,
          color: Colors.common.gray3,
          transform: 'none',
        },
        'label.Mui-error': {
          color: 'inherit',
        },
        '.Mui-error': {
          input: {
            borderWidth: 1,
            borderColor: Colors.error.main,
          },
        },
        '.MuiInputBase-root': {
          paddingTop: 8,
          height: ownerState.multiline ? 'inherit' : 48,
        },

        input: {
          ...typography.avenir16,
          backgroundColor: Colors.common.slate1,
          borderColor: Colors.common.slate3,
          borderStyle: 'solid',
          borderWidth: 1,
          borderRadius: 6,
          padding: '6px 12px',
          transition: 'border-color ease-in-out .15s,box-shadow ease-in-out .15s',

          '&:focus': {
            borderWidth: 2,
            marginLeft: -1,
            borderColor: Colors.brand.color,
          },
          '&::placeholder': {
            ...typography.avenir14,
          },
        },

        textarea: {
          ...typography.avenir16,
          backgroundColor: Colors.common.slate1,
          borderColor: Colors.common.slate3,
          borderStyle: 'solid',
          borderWidth: 1,
          borderRadius: 6,
          padding: '6px 12px',
          transition: 'border-color ease-in-out .15s,box-shadow ease-in-out .15s',

          '&:focus': {
            borderWidth: 2,
            margin: -1,
            borderColor: Colors.brand.color,
          },
          '&::placeholder': {
            ...typography.avenir14,
          },
        },

        '.MuiFormHelperText-root': {
          paddingTop: 8,
        },
      }),
    },
  },
  MuiTooltip: {
    defaultProps: {
      placement: 'bottom',
      arrow: true,
    },
    styleOverrides: {
      tooltip: {
        ...typography.avenir14,
        backgroundColor: 'white',
        border: '1px solid',
        borderColor: Colors.common.slate3,
        borderRadius: 6,
        boxShadow: '0 5px 10px rgb(0 0 0 / 20%)',
        color: 'rgba(0, 0, 0, 0.87)',
        padding: 14,
      },
      arrow: {
        '&:before': {
          border: '1px solid',
          borderColor: Colors.common.slate3,
        },
        color: 'white',
        fontSize: 20,
      },
    },
  },
  MuiSelect: {
    defaultProps: {
      fullWidth: true,
      size: 'small',
    },
  },
  MuiLink: {
    styleOverrides: {
      root: {
        textDecoration: 'none',
      },
    },
  },
}

export default createTheme({
  components,
  palette: Colors,
  typography,
})
